// @flow
import type {Element, Node} from 'react'
import React, {useState, useEffect} from 'react'
import classNames from 'classnames'
import {widthXs} from 'styles/variables-breakpoints.scss'
import PremiumCallout, {type CalloutType} from 'components/ui/PremiumCallout'
import AppLink from 'components/ui/AppLink'
import IconContainer from 'components/SideBar/ui/IconContainer'
import styles from './index.scss'
import useUniqueId from '../../../../utils/hooks/useUniqueId'
import {toggleSidebar} from '../../globalControl'

export type RenderActionButtonProps = {
  dark: boolean,
}

type Props = $ReadOnly<{
  title: string,
  renderActionButton: ?(RenderActionButtonProps) => Node,
  dark: boolean,
  dataTurboFrame: ?string,
  renderIcon: ?() => Node,
  href: ?string,
  target: ?string,
  onClick: ?(SyntheticMouseEvent<*>) => void,
  renderBadge: ?Node,
  className: ?string,
  noActiveClass?: boolean,
  premiumCallout?: ?CalloutType,
  premiumCalloutLinkable?: boolean
}>

function SectionItem({
  title,
  renderActionButton,
  dark,
  dataTurboFrame,
  renderIcon,
  href,
  target,
  onClick,
  renderBadge,
  className,
  noActiveClass,
  premiumCallout,
  premiumCalloutLinkable,
}: Props): Element<"li"> {
  const itemClass = classNames(styles.item, className)

  const [isNarrowScreen, setNarrowScreen] = useState(window.innerWidth < widthXs)

  useEffect(() => {
    const handleResize = () => {
      setNarrowScreen(window.innerWidth < widthXs)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const linkClass = classNames(styles.link, {
    [styles.darkButton]: dark,
  })

  const linkHighlightedClass = noActiveClass
    ? ''
    : classNames({
      [styles.highlightedButton]: !dark,
      [styles.darkHighlightedButton]: dark,
    })

  const buttonClass = classNames(styles.button, {
    [styles.darkButton]: dark,
  })

  const maybeRenderIcon = () => (
    renderIcon && (
      <span className={styles.itemIcon} test-id="sidebar-section-item-icon">
        <IconContainer size="sm">
          {renderIcon()}
        </IconContainer>
      </span>
    )
  )

  const maybeRenderBadge = () => (
    renderBadge && (
      <span className={styles.itemIcon}>
        {renderBadge}
      </span>
    )
  )

  const [id] = useUniqueId()

  return (
    <li className={itemClass} test-id="side-bar-section-item">
      {href && (
        <AppLink
          highlightActive
          className={linkClass}
          activeClassName={linkHighlightedClass}
          href={href}
          target={target}
          data-turbo={(dataTurboFrame ? 'true' : null)}
          data-turbo-frame={dataTurboFrame}
          onClick={isNarrowScreen ? toggleSidebar : onClick}
          disabled={!!premiumCallout && !premiumCalloutLinkable}
          test-id="section-item-link"
        >
          {maybeRenderIcon()}
          <div className={styles.itemTitle} id={id}>
            {title}
            {premiumCallout && <PremiumCallout callout={premiumCallout} enabled target={id} />}
          </div>
          {maybeRenderBadge()}
        </AppLink>
      )}
      {!href && onClick && (
        <button
          className={buttonClass}
          onClick={onClick}
          test-id="section-item-button"
          type="button"
          disabled={!!premiumCallout && !premiumCalloutLinkable}
        >
          {maybeRenderIcon()}
          <div className={styles.itemTitle}>
            {title}
          </div>
        </button>
      )}
      {renderActionButton && (
        <div className={styles.actionButton} test-id="section-item-action-button">
          {renderActionButton({dark})}
        </div>
      )}
    </li>
  )
}

SectionItem.defaultProps = {
  dark: false,
  dataTurboFrame: null,
  renderActionButton: null,
  renderIcon: null,
  href: null,
  onClick: null,
  renderBadge: null,
  noActiveClass: false,
  target: null,
  className: null,
  premiumCalloutLinkable: false,
}

export default SectionItem
